// src/utils/authFetch.js
let refreshPromise = null;
let refreshTimeoutId = null;

export const REFRESH_ENDPOINT = '/api/accounts/token/refresh/';
const SKEW_SECONDS = 45; // за сколько секунд до exp обновлять access

function decodeJwt(token) {
  try {
    const [, payload] = token.split('.');
    return JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    );
  } catch {
    return null;
  }
}

function getExp(token) {
  const p = decodeJwt(token);
  return p?.exp || 0; // unix seconds
}

function isExpiring(token, skew = SKEW_SECONDS) {
  if (!token) return true;
  const exp = getExp(token);
  if (!exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return exp - now <= skew;
}

function scheduleProactiveRefresh(refreshEndpoint = REFRESH_ENDPOINT) {
  clearTimeout(refreshTimeoutId);
  const access = localStorage.getItem('access_token');
  const exp = getExp(access);
  if (!exp) return;
  const nowMs = Date.now();
  const delayMs = Math.max(exp * 1000 - nowMs - SKEW_SECONDS * 1000, 0);
  refreshTimeoutId = setTimeout(() => {
    void ensureFreshAccess(refreshEndpoint).catch(() => {});
  }, delayMs);
}

async function doRefresh(refreshEndpoint = REFRESH_ENDPOINT) {
  const refresh = localStorage.getItem('refresh_token');
  if (!refresh) throw new Error('no_refresh_token');

  const resp = await fetch(refreshEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ refresh })
  });
  if (!resp.ok) throw new Error('refresh_failed');

  const data = await resp.json();
  if (!data.access) throw new Error('no_access_in_response');

  localStorage.setItem('access_token', data.access);
  if (data.refresh) localStorage.setItem('refresh_token', data.refresh);

  scheduleProactiveRefresh(refreshEndpoint);
  return data.access;
}

// Гарантирует, что refresh выполняется единожды для всех ожидающих
async function ensureFreshAccess(refreshEndpoint = REFRESH_ENDPOINT) {
  if (!refreshPromise) {
    refreshPromise = doRefresh(refreshEndpoint)
      .catch((err) => {
        // Жёсткий logout при провале refresh
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        clearTimeout(refreshTimeoutId);
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Инициировать таймеры при старте приложения (вызвать один раз в App)
export function initAuthTimers(refreshEndpoint = REFRESH_ENDPOINT) {
  const access = localStorage.getItem('access_token');
  if (isExpiring(access)) {
    ensureFreshAccess(refreshEndpoint).catch(() => {});
  } else {
    scheduleProactiveRefresh(refreshEndpoint);
  }
  // Синхронизация вкладок
  window.addEventListener('storage', (e) => {
    if (e.key === 'access_token' || e.key === 'refresh_token') {
      scheduleProactiveRefresh(refreshEndpoint);
    }
  });
}

// Удобные методы для логина/логаута
export function setTokens({ access, refresh }, refreshEndpoint = REFRESH_ENDPOINT) {
  if (access) localStorage.setItem('access_token', access);
  if (refresh) localStorage.setItem('refresh_token', refresh);
  scheduleProactiveRefresh(refreshEndpoint);
}
export function clearTokens() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  clearTimeout(refreshTimeoutId);
}

// Основной обёртка над fetch
export async function authFetch(url, options = {}, refreshEndpoint = REFRESH_ENDPOINT) {
  let accessToken = localStorage.getItem('access_token');

  // Проактивный refresh перед запросом
  if (isExpiring(accessToken)) {
    try {
      accessToken = await ensureFreshAccess(refreshEndpoint);
    } catch {
      return { data: null, status: 401 };
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
    ...(options.headers || {})
  };

  let response = await fetch(url, { ...options, headers });

  if (response.status !== 401) {
    let data = null;
    try { data = await response.json(); } catch {}
    return { data, status: response.status };
  }

  // Разовая попытка освежить токен и повторить запрос
  try {
    const newAccess = await ensureFreshAccess(refreshEndpoint);
    const retryHeaders = { ...headers, 'Authorization': `Bearer ${newAccess}` };
    response = await fetch(url, { ...options, headers: retryHeaders });
  } catch {
    return { data: null, status: 401 };
  }

  let data = null;
  try { data = await response.json(); } catch {}
  return { data, status: response.status };
}
